import { z } from 'zod';

/**
 * The signup quiz's question content (TAPS 1: motivation and goal, then the
 * TAPS 2 screening questions), in the shape a backend will serve. Kept here,
 * beside the substance step's program options, so the client renders it
 * generically and never names a program. Structure only: the words live in the
 * signup catalog. Answer names and values are the Galapagos intake flow's, so a
 * signup answer reads exactly like one given in the app.
 */

/** A choice: the value it saves. Its label and description live in the signup catalog. */
export const signupQuizOptionSchema = z
  .object({
    value: z.string().min(1),
  })
  .passthrough();

/**
 * A quiz question: what it saves and the values it offers. Its words (the
 * title, the line under it and each choice's label and description) live in
 * the signup catalog, keyed by answer name and option value, so one structure
 * serves every language.
 */
export const signupQuizQuestionSchema = z
  .object({
    /** The intake session answer this question fills. */
    answerName: z.string().min(1),
    /** `list` stores the choice as a one-item array, for answers the intake keeps as arrays. */
    saveAs: z.enum(['value', 'list']),
    options: z.array(signupQuizOptionSchema).min(1),
  })
  .passthrough();

/**
 * A question whose words can differ by the member's primary program. The
 * catalog holds any program's own lines beside the default ones; the priority
 * here decides which program is primary.
 */
export const signupQuizWordedQuestionSchema = signupQuizQuestionSchema
  .extend({
    /** Program keys, highest first; the member's first match decides the wording. */
    programPriority: z.array(z.string().min(1)),
  })
  .passthrough();

/** A TAPS 2 choice: the same shape as any quiz choice. */
export const signupScreeningOptionSchema = signupQuizOptionSchema;

/** A TAPS 2 screening question: the same shape as any quiz question. */
export const signupScreeningQuestionSchema = signupQuizQuestionSchema;

/**
 * TAPS 2: the screening questions asked about the member's main program only.
 * A program with no chain asks nothing.
 */
export const signupScreeningContentSchema = z
  .object({
    /** Program keys, highest first; the member's first match is the main program. */
    programPriority: z.array(z.string().min(1)),
    /** The questions per program key, in ask order; the flow has two steps to ask them on. */
    chains: z.record(z.string(), z.array(signupScreeningQuestionSchema).min(1).max(2)),
  })
  .passthrough();

export const signupQuizContentSchema = z
  .object({
    motivation: signupQuizQuestionSchema,
    goal: signupQuizWordedQuestionSchema,
    /** Optional so content without screening still parses; no section asks nothing. */
    taps2: signupScreeningContentSchema.optional(),
  })
  .passthrough();

export type SignupQuizOption = z.infer<typeof signupQuizOptionSchema>;
export type SignupQuizQuestion = z.infer<typeof signupQuizQuestionSchema>;
export type SignupQuizWordedQuestion = z.infer<typeof signupQuizWordedQuestionSchema>;
export type SignupScreeningOption = z.infer<typeof signupScreeningOptionSchema>;
export type SignupScreeningQuestion = z.infer<typeof signupScreeningQuestionSchema>;
export type SignupScreeningContent = z.infer<typeof signupScreeningContentSchema>;
export type SignupQuizContent = z.infer<typeof signupQuizContentSchema>;

/**
 * TAPS 2: v9 main path 1.13 / 1.13b. Answer names and values are the program
 * catalog's, except the stimulants question, which has no catalog entry yet and
 * so is stored but not scored. Structure only: the words live in the signup
 * catalog under `quiz.taps2`.
 */
const SIGNUP_TAPS2_SCREENING: SignupScreeningContent = {
  programPriority: ['opioid', 'stimulants', 'alcohol', 'cannabis', 'tobacco'],
  chains: {
    alcohol: [
      {
        answerName: 'taps2_alcohol_concern',
        saveAs: 'value',
        options: [{ value: 'yes' }, { value: 'no' }],
      },
    ],
    cannabis: [
      {
        answerName: 'taps2_cannabis_concern',
        saveAs: 'value',
        options: [{ value: 'yes' }, { value: 'no' }],
      },
    ],
    opioid: [
      {
        answerName: 'taps2_opioid_misuse',
        saveAs: 'value',
        options: [{ value: 'yes' }, { value: 'no' }],
      },
      {
        answerName: 'taps2_opioid_concern',
        saveAs: 'value',
        options: [{ value: 'yes' }, { value: 'no' }],
      },
    ],
    stimulants: [
      {
        answerName: 'taps2_stimulants_concern',
        saveAs: 'value',
        options: [{ value: 'yes' }, { value: 'no' }],
      },
    ],
    tobacco: [
      {
        answerName: 'taps2_smoke_timing',
        saveAs: 'value',
        options: [{ value: 'within_30' }, { value: 'over_60' }],
      },
    ],
  },
};

/**
 * Figma v9 main path 1.9 (motivation), 1.10 (goal) and the TAPS 2 screening
 * structure. The goal's default lines are the intake flow's neutral wording;
 * v9's alcohol screen is the one program wording so far, held in the catalog.
 * The web signup seam serves it as is, real and local fake alike.
 */
export const SIGNUP_QUIZ_CONTENT: SignupQuizContent = {
  motivation: {
    answerName: 'motivation',
    saveAs: 'list',
    options: [
      { value: 'health' },
      { value: 'relationship' },
      { value: 'control' },
      { value: 'provider' },
      { value: 'curious' },
      { value: 'other' },
    ],
  },
  goal: {
    answerName: 'goal',
    saveAs: 'value',
    options: [
      { value: 'stop' },
      { value: 'cut_back' },
      { value: 'understand' },
      { value: 'exploring' },
      { value: 'sober' },
      { value: 'unsure' },
    ],
    programPriority: ['opioid', 'stimulants', 'alcohol', 'cannabis', 'tobacco'],
  },
  taps2: SIGNUP_TAPS2_SCREENING,
};
